import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import * as utils from 'common/utils';
import FlexContainer from 'common/components/atoms/FlexContainer';
import InfoTooltip from 'common/components/atoms/InfoTooltip';
import { bearingFrequenciesDescription } from 'common/constants';

const TagInfoContainer = styled(FlexContainer)`
  margin-left: 25px;
  min-height: 24px;
  flex-wrap: wrap;
`;

const InfoText = styled.span`
  display: block;
  font-size: 12px;
  color: #999B95;
  padding-bottom: 0.4em;
  b {
    font-weight: 600;
    color: black;
  }
  margin-right: 2em;
`;

// The tooltip trigger is a button, so the line it sits on has to align them.
const AlarmNote = styled(InfoText)`
  display: inline-flex;
  align-items: center;
`;

const GreenSpan = styled.span`
  color: #91b846;
  font-family: 'Petasense Open Sans';
  &:hover {
    cursor: pointer;
  }
`;

const ChartItemTagInfo = ({
  chartType,
  config,
  tag,
  noAlarmParameter,
  showOverlay,
  waveAndSpecSelection,
  t
}) => {
  if (tag.structure_type !== 'vector' || chartType === 'waterfall_spectrum') {
    return null;
  }

  return (
    <TagInfoContainer>
      {noAlarmParameter && (
        <AlarmNote>
          {t('assetHierarchy.chartItem.info.noAlarmParameter', {
            defaultValue: 'No alarm for this measure'
          })}
          <InfoTooltip
            label={t('assetHierarchy.chartItem.info.noAlarmParameterLabel', {
              defaultValue: 'Why this chart shows no alarm lines'
            })}
            content={t('assetHierarchy.chartItem.info.noAlarmParameterHelp', {
              defaultValue: 'No alarm parameter exists for this measure, so the chart draws no alarm lines. A Petasense admin can add one under Admin Dashboard \u2192 Alarm Parameters.'
            })}
          />
        </AlarmNote>
      )}
      {config.spectrum && config.spectrum.fmax && (
        <>
          {waveAndSpecSelection && (
            <InfoText>
              <b>{utils.formatDate(waveAndSpecSelection.x)}</b>
            </InfoText>
          )}
          <InfoText>
            {t('assetHierarchy.chartItem.info.fmaxLabel', { defaultValue: 'Fmax:' })}&nbsp;
            <b>
              {config.spectrum.fmax} {config.spectrum.freq_units}
            </b>
          </InfoText>
          <InfoText>
            {t('assetHierarchy.chartItem.info.linesOfResolutionLabel', { defaultValue: 'Lines of resolution:' })}{' '}
            <b>{config.spectrum.lines_of_resolution}</b>
          </InfoText>
        </>
      )}
      {config.waveform && config.waveform.broadband_features && !showOverlay && (
        <>
          {config.waveform.broadband_features.rms != null && (
            <InfoText>
              {t('assetHierarchy.chartItem.info.rmsLabel', { defaultValue: 'RMS:' })}&nbsp;
              <b>
                {config.waveform.broadband_features.rms.toFixed(2)}&nbsp;
                {config.waveform.amp_units}
              </b>
            </InfoText>
          )}
          {config.spectrum && config.spectrum.shaft_speed && (
            <InfoText>
              {t('assetHierarchy.chartItem.info.shaftSpeedLabel', { defaultValue: 'Shaft Speed:' })}&nbsp;
              <b>{config.spectrum.shaft_speed} {config.spectrum.freq_units}</b>
            </InfoText>
          )}
          {config.waveform.broadband_features.peak != null && (
            <InfoText>
              {t('assetHierarchy.chartItem.info.peakLabel', { defaultValue: 'Peak:' })}&nbsp;
              <b>
                {config.waveform.broadband_features.peak.toFixed(2)}&nbsp;
                {config.waveform.amp_units}
              </b>
            </InfoText>
          )}
          {config.waveform.broadband_features.p2p != null && (
            <InfoText>
              {t('assetHierarchy.chartItem.info.peakToPeakLabel', { defaultValue: 'Peak to Peak:' })}&nbsp;
              <b>
                {config.waveform.broadband_features.p2p.toFixed(2)}&nbsp;
                {config.waveform.amp_units}
              </b>
            </InfoText>
          )}
          {config.waveform.broadband_features.crest_factor != null && (
            <InfoText>
              {t('assetHierarchy.chartItem.info.crestFactorLabel', { defaultValue: 'Crest Factor:' })}&nbsp;
              <b>{config.waveform.broadband_features.crest_factor.toFixed(2)}</b>
            </InfoText>
          )}
        </>
      )}
      {tag && tag.forcingFrequencies && tag.forcingFrequencies.items.map((item) => {
        if (!bearingFrequenciesDescription.includes(item.description)) return null;
        return (
          <InfoText key={item.marker}>
            {item.marker.toUpperCase()}:&nbsp;
            <b>{item.frequency.toFixed(1)} {item.freq_units}</b>
          </InfoText>
        );
      })}
    </TagInfoContainer>
  );
};

ChartItemTagInfo.propTypes = {
  chartType: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  tag: PropTypes.object.isRequired,
  noAlarmParameter: PropTypes.bool,
  showOverlay: PropTypes.bool,
  waveAndSpecSelection: PropTypes.object,
  t: PropTypes.func.isRequired,
};

ChartItemTagInfo.defaultProps = {
  noAlarmParameter: false,
  showOverlay: false,
  waveAndSpecSelection: null,
};

export { GreenSpan };
export default withTranslation()(ChartItemTagInfo);
